import { useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'
import { isCustomSiteHost } from '@reptree/shared'
import { applyUpdate, dismissUpdate, getUpdateState, showsUpdateNotice, subscribeUpdate, updateAvailable } from '../../lib/app-update'
import { isBusy, subscribeBusy } from '../../lib/busy'
import { anyModalOpen, subscribeModalOpen } from '../../lib/modal-open'
import { Button } from '../ui'
import { ToastCard } from './ToastCard'

const available = () => updateAvailable(getUpdateState())

/**
 * "A new version of RepTree is ready." Raised by lib/app-update.ts when the deployed build differs from the one
 * this page is running. The person picks the moment: Refresh loads the live build, Later hides the notice until
 * the next deploy. Refresh waits while an upload runs, and the whole notice stays out of the way of an open
 * dialog. It never takes focus; the wrapper is a persistent status region so its arrival is announced.
 */
export function UpdateToast() {
  const ready = useSyncExternalStore(subscribeUpdate, available)
  const busy = useSyncExternalStore(subscribeBusy, isBusy)
  const modalOpen = useSyncExternalStore(subscribeModalOpen, anyModalOpen)
  const { pathname } = useLocation()
  const show = ready && !modalOpen && showsUpdateNotice(pathname, isCustomSiteHost(window.location.hostname))

  return (
    <div role="status">
      {show && (
        <ToastCard>
          <p className="font-medium">A new version of RepTree is ready.</p>
          <p className="mt-0.5 text-xs text-[color:rgb(var(--muted))]">
            {busy ? 'Refresh once your upload finishes.' : 'Refresh to load the latest.'}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="subtle" className="min-h-11 justify-center px-4" onClick={dismissUpdate}>
              Later
            </Button>
            <Button className="min-h-11 justify-center" onClick={applyUpdate} disabled={busy}>
              Refresh
            </Button>
          </div>
        </ToastCard>
      )}
    </div>
  )
}
